import { CircleX, TriangleAlert } from "lucide-react";
import { Fragment, useMemo } from "react";
import { RAID_SIZE } from "@/shared/lib/raid";
import { STATUS_GREEN, STATUS_RED } from "@/shared/lib/statusColors";
import { cn } from "@/shared/lib/utils";
import {
  buildBossPlayerLists,
  ruleFailuresForPlayers,
  type VisibleBoss,
} from "../setup.bosses";
import type { SetupResponse } from "../setup.types";
import { RaidGap } from "./BossMatrixCells";

export function BossRulesFooter({
  data,
  bosses,
}: {
  data: SetupResponse;
  bosses: VisibleBoss[];
}) {
  const playerLists = useMemo(() => buildBossPlayerLists(data), [data]);
  // Rule failures depend only on the roster, not on hover state — memoize so
  // hovering a cell (which re-renders this footer) doesn't re-run evaluateRules.
  const bossFailures = useMemo(
    () =>
      new Map(
        bosses.map((boss) => [
          boss.id,
          // Skipped bosses aren't run, so there are no rules to enforce on them.
          boss.skipped
            ? []
            : ruleFailuresForPlayers(playerLists.get(boss.id) ?? []),
        ]),
      ),
    [bosses, playerLists],
  );

  return (
    <tfoot>
      <tr>
        <td aria-hidden className="min-w-48 bg-background" />
        <td aria-hidden className="min-w-16 bg-background" />
        {bosses.map((boss, index) => {
          const count = playerLists.get(boss.id)?.length ?? 0;
          return (
            <Fragment key={boss.id}>
              {boss.isGroupStart && index > 0 && <RaidGap />}
              <td className="h-9 min-w-28 p-0">
                {/* Skipped bosses aren't being run, so there's no roster to count. */}
                {boss.skipped ? (
                  <div className="h-9 w-full bg-muted/20" />
                ) : (
                  <div
                    className={cn(
                      "flex h-9 w-full items-center justify-center text-xs font-semibold tabular-nums",
                      count >= RAID_SIZE ? STATUS_GREEN : STATUS_RED,
                    )}
                  >
                    {count}
                  </div>
                )}
              </td>
            </Fragment>
          );
        })}
      </tr>
      <tr>
        <td aria-hidden className="min-w-48 bg-background" />
        <td aria-hidden className="min-w-16 bg-background" />
        {bosses.map((boss, index) => {
          const failures = bossFailures.get(boss.id) ?? [];
          return (
            <Fragment key={boss.id}>
              {boss.isGroupStart && index > 0 && <RaidGap />}
              <td className="min-w-28 p-1.5 align-top">
                <div className="mx-auto w-fit max-w-full space-y-1 text-left">
                  {failures.map((failure) => {
                    const Icon =
                      failure.severity === "error" ? CircleX : TriangleAlert;
                    return (
                      <div
                        key={failure.id}
                        className={cn(
                          "flex items-start gap-1 text-[11px] leading-tight",
                          failure.severity === "error"
                            ? "text-destructive"
                            : "text-amber-500",
                        )}
                      >
                        <Icon className="mt-px size-3 shrink-0" aria-hidden />
                        <span data-capture-nowrap>{failure.label}</span>
                      </div>
                    );
                  })}
                </div>
              </td>
            </Fragment>
          );
        })}
      </tr>
    </tfoot>
  );
}
